/**
 * @ipc/admin-ui · shared/lib/twa — типизированный фасад над Telegram Web App SDK.
 *
 * SDK-скрипт (https://telegram.org/js/telegram-web-app.js) подключается в index.html
 * и кладёт API в `window.Telegram.WebApp` ДО рендера. Здесь — тонкая типизированная
 * обёртка: определить, что приложение открыто внутри Telegram Mini App, и достать
 * сырой `initData` для авто-входа. Вне Telegram все функции — no-op (initData = '').
 *
 * initData — URL-encoded query-строка, которую бэк проверяет по алгоритму WebAppData
 * (см. apps/backend/.../twa-verify.ts). Клиент её НЕ разбирает и НЕ доверяет ей —
 * только транспортирует на сервер как есть.
 *
 * Fullscreen (Bot API 8.0+): `requestFullscreen()` уводит webview под системные
 * бары/чат-хром. SDK выставляет CSS-переменные:
 *   --tg-safe-area-inset-{top,right,bottom,left}
 *   --tg-content-safe-area-inset-{top,right,bottom,left}
 * Контент обязан их суммировать (globals.css · `.twa-safe-insets`), иначе UI
 * уезжает под notch / кнопку закрытия / home indicator.
 */

interface SafeAreaInset {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface TelegramWebApp {
  initData: string;
  version: string;
  platform?: string;
  colorScheme?: 'light' | 'dark';
  ready(): void;
  expand(): void;
  close?(): void;
  isExpanded?: boolean;
  isFullscreen?: boolean;
  isVersionAtLeast?(version: string): boolean;
  disableVerticalSwipes?(): void;
  requestFullscreen?(): void;
  exitFullscreen?(): void;
  setHeaderColor?(color: 'bg_color' | 'secondary_bg_color' | string): void;
  setBackgroundColor?(color: 'bg_color' | 'secondary_bg_color' | string): void;
  setBottomBarColor?(color: 'bg_color' | 'secondary_bg_color' | 'bottom_bar_bg_color' | string): void;
  onEvent?(eventType: string, eventHandler: () => void): void;
  offEvent?(eventType: string, eventHandler: () => void): void;
  safeAreaInset?: SafeAreaInset;
  contentSafeAreaInset?: SafeAreaInset;
  HapticFeedback?: {
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
    impactOccurred?(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
  };
  themeParams?: Record<string, string>;
  MainButton?: {
    text: string;
    isVisible: boolean;
    show(): void;
    hide(): void;
    setText(text: string): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
    showProgress?(leaveActive?: boolean): void;
    hideProgress?(): void;
    enable?(): void;
    disable?(): void;
  };
}

interface TelegramNamespace {
  WebApp?: TelegramWebApp;
}

declare global {
  interface Window {
    Telegram?: TelegramNamespace;
  }
}

/** Цвета оболочки admin-ui (совпадают с tokens в globals.css). */
const TWA_BG_DARK = '#0a0a0b';
const TWA_BG_LIGHT = '#f3f4f6';

/** Возвращает объект Telegram WebApp, если он доступен и корректной формы, иначе null. */
export function getWebApp(): TelegramWebApp | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const webApp = window.Telegram?.WebApp;
  if (!webApp || typeof webApp.initData !== 'string') {
    return null;
  }
  return webApp;
}

/** Открыто ли приложение внутри Telegram Mini App с непустым initData. */
export function isTelegramWebApp(): boolean {
  const webApp = getWebApp();
  return webApp !== null && webApp.initData.length > 0;
}

/**
 * Compact TWA-режим: внутри Telegram Mini App (initData) либо явный ?twa=1
 * (кнопка WebApp из бота). Нужен для урезанного layout без сайдбара.
 */
export function isTwaCompactMode(): boolean {
  if (isTelegramWebApp()) {
    return true;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return new URLSearchParams(window.location.search).get('twa') === '1';
  } catch {
    return false;
  }
}

/** Сырой initData для отправки на /admin/auth/telegram-twa. Пустая строка вне Telegram. */
export function getInitDataRaw(): string {
  return getWebApp()?.initData ?? '';
}

/**
 * Прокидывает safe-area insets в CSS-переменные на `:root`.
 * SDK обычно делает это сам; дублируем для надёжности и на событиях change.
 */
function syncSafeAreaCssVars(webApp: TelegramWebApp): void {
  if (typeof document === 'undefined') {
    return;
  }
  const root = document.documentElement;
  const safe = webApp.safeAreaInset;
  const content = webApp.contentSafeAreaInset;

  const setPx = (name: string, value: number | undefined): void => {
    root.style.setProperty(name, `${typeof value === 'number' && Number.isFinite(value) ? value : 0}px`);
  };

  setPx('--tg-safe-area-inset-top', safe?.top);
  setPx('--tg-safe-area-inset-right', safe?.right);
  setPx('--tg-safe-area-inset-bottom', safe?.bottom);
  setPx('--tg-safe-area-inset-left', safe?.left);

  setPx('--tg-content-safe-area-inset-top', content?.top);
  setPx('--tg-content-safe-area-inset-right', content?.right);
  setPx('--tg-content-safe-area-inset-bottom', content?.bottom);
  setPx('--tg-content-safe-area-inset-left', content?.left);

  // Суммарные inset'ы — то, что реально «съедает» chrome + device.
  const top = (safe?.top ?? 0) + (content?.top ?? 0);
  const right = (safe?.right ?? 0) + (content?.right ?? 0);
  const bottom = (safe?.bottom ?? 0) + (content?.bottom ?? 0);
  const left = (safe?.left ?? 0) + (content?.left ?? 0);
  setPx('--ipc-twa-inset-top', top);
  setPx('--ipc-twa-inset-right', right);
  setPx('--ipc-twa-inset-bottom', bottom);
  setPx('--ipc-twa-inset-left', left);

  root.dataset.twaFullscreen = webApp.isFullscreen === true ? '1' : '0';
}

function bindChromeColors(webApp: TelegramWebApp): void {
  try {
    const isDark = document.documentElement.classList.contains('dark');
    const bg = isDark ? TWA_BG_DARK : TWA_BG_LIGHT;
    webApp.setHeaderColor?.(bg);
    webApp.setBackgroundColor?.(bg);
    webApp.setBottomBarColor?.(bg);
  } catch {
    // старые клиенты / hex не принят — игнор
  }
}

function requestFullscreenIfSupported(webApp: TelegramWebApp): void {
  if (typeof webApp.requestFullscreen !== 'function') {
    return;
  }
  if (webApp.isVersionAtLeast !== undefined && !webApp.isVersionAtLeast('8.0')) {
    return;
  }
  if (webApp.isFullscreen === true) {
    return;
  }
  try {
    webApp.requestFullscreen();
  } catch {
    // WebAppMethodUnsupported / отказ клиента — остаёмся в expand()
  }
}

let twaListenersBound = false;

/**
 * Инициализация окружения Mini App: ready + expand + fullscreen + safe-area.
 * Вызывать один раз при старте. Вне Telegram — no-op.
 */
export function initTelegramWebApp(): void {
  const webApp = getWebApp();
  if (!webApp) {
    return;
  }

  webApp.ready();
  webApp.expand();

  if (typeof webApp.disableVerticalSwipes === 'function' && webApp.isVersionAtLeast?.('7.7')) {
    webApp.disableVerticalSwipes();
  }

  bindChromeColors(webApp);
  requestFullscreenIfSupported(webApp);
  syncSafeAreaCssVars(webApp);

  document.documentElement.classList.add('twa-mode');

  if (!twaListenersBound && typeof webApp.onEvent === 'function') {
    twaListenersBound = true;
    const resync = (): void => {
      syncSafeAreaCssVars(webApp);
    };
    webApp.onEvent('safeAreaChanged', resync);
    webApp.onEvent('contentSafeAreaChanged', resync);
    webApp.onEvent('fullscreenChanged', resync);
    webApp.onEvent('viewportChanged', resync);
  }
}

/** Haptic success/error — no-op вне TWA. */
export function twaHaptic(type: 'success' | 'error' | 'warning'): void {
  try {
    getWebApp()?.HapticFeedback?.notificationOccurred(type);
  } catch {
    // ignore
  }
}

/** Закрыть Mini App (после создания ссылки — опционально). */
export function closeTelegramWebApp(): void {
  try {
    getWebApp()?.close?.();
  } catch {
    // ignore
  }
}
